<template>
  <header ref="headerNavigation"
          class="outer-wrapper"
          :class="{ 'fixed-top': !notFixed, 'no-shadow': noShadow }"
  >
    <div>
      <b-container
        class="header-nav-wrapper centered d-flex justify-content-between align-items-center p-0"
        :class="{ 'no-flex': fullTitle }"
      >
        <div ref="leftActionWrapper" :class="{ 'flex-grow-0': leftRightActionSimilarWidth }">
          <slot name="left-actions">
            <b-button
              v-if="!hideBackAction"
              id="header-go-back-button"
              class="pr-0 pl-3 d-flex align-items-center hitbox-min-size"
              variant="link"
              data-cy="header-go-back"
              @click="handleBackAction"
            >
              <sdui-icon class="icon-chevron-back-o primary icon-28" />
              <span v-if="backLabel" class="back-label">{{ backLabel }}</span>
              <span v-else class="sr-only">{{ $t('global.back') }}</span>
            </b-button>
          </slot>
        </div>

        <slot name="title">
          <h1
            v-if="title"
            class="title mb-0 text-center font-weight-bold fz-base w-100 p-1"
            :class="{ 'no-flex': !$hasSlotContent($slots['right-actions']) }"
            data-test="title"
          >
            {{ title }}
          </h1>
        </slot>

        <div
          v-if="$hasSlotContent($slots['right-actions'])"
          ref="rightActionWrapper"
          class="right-actions d-flex justify-content-end align-items-center text-break-hyphens"
          :class="{
            'right-actions--max-width': title || $hasSlotContent($slots['left-actions']),
            'no-flex': !title && !$hasSlotContent($slots['left-actions']),
            'flex-grow-0': leftRightActionSimilarWidth,
          }"
        >
          <slot name="right-actions" />
        </div>
        <span v-else class="hitbox-min-size no-flex" />
      </b-container>

      <slot name="bottom-actions" />

      <b-container v-if="progress" class="position-relative p-0">
        <SProgress v-model="localProgress"
                   class="position-absolute top-0 left-0 w-100"
                   :label="progressLabel"
                   :max="maxSteps"
        />
      </b-container>
    </div>
  </header>
</template>

<script>
import SProgress from 'src/components/base/SProgress.vue'

export default {
  name: 'HeaderNavigation',
  components: { SProgress },
  props: {
    progress: {
      type: Number,
      default: 0,
    },

    progressLabel: {
      type: String,
      default: '',
    },

    maxSteps: {
      type: Number,
      default: 0,
    },

    backLabel: {
      type: String,
      default: '',
    },

    title: {
      type: String,
      default: '',
    },

    hideBackAction: {
      type: Boolean,
      default: false,
    },

    notFixed: {
      type: Boolean,
      default: false,
    },

    fullTitle: {
      type: Boolean,
      default: false,
    },

    noShadow: {
      type: Boolean,
      default: false,
    },

    reInitEntryFocus: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    leftActionWrapperWidth: 0,
    rightActionWrapperWidth: 0,
  }),

  computed: {
    localProgress: {
      get() {
        return this.progress
      },

      set() {},
    },

    leftRightActionSimilarWidth() {
      if (this.leftActionWrapperWidth === 0 && this.rightActionWrapperWidth === 0) return false
      // if both widths are within 25px of each other, we consider them similar
      return Math.abs(this.leftActionWrapperWidth - this.rightActionWrapperWidth) <= 25
    },
  },

  emits: ['backAction'],

  methods: {
    handleBackAction() {
      // TODO VUE 3: use this.$attrs.onBackAction
      // eslint-disable-next-line vue/no-deprecated-dollar-listeners-api
      if (typeof this.$listeners.backAction === 'function') {
        return this.$emit('backAction')
      }
      this.$router.go(-1)
    },

    setInitEntryFocus() {
      const headerNavWrapperEl = this.$refs.headerNavigation
      if (!headerNavWrapperEl) return
      headerNavWrapperEl.setAttribute('tabindex', '-1')
      headerNavWrapperEl.focus()
      headerNavWrapperEl.removeAttribute('tabindex')
    },

    setLeftRightWrapperWidth() {
      const hasLeftActionsSlot = this.$hasSlotContent(this.$slots['left-actions'])

      this.leftActionWrapperWidth =
        (!hasLeftActionsSlot && !this.hideBackAction
          ? this.$refs?.leftActionWrapper?.firstChild?.clientWidth
          : this.$refs?.leftActionWrapper?.firstChild?.firstChild?.clientWidth) || 0

      this.rightActionWrapperWidth = this.$refs?.rightActionWrapper?.firstChild?.clientWidth || 0
    },
  },

  watch: {
    reInitEntryFocus(newValue, oldValue) {
      if (oldValue === newValue) return
      this.setInitEntryFocus()
    },
  },

  async mounted() {
    await this.$nextTick()
    this.setLeftRightWrapperWidth()

    window.addEventListener('resize', this.setLeftRightWrapperWidth)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.setLeftRightWrapperWidth)
  },
}
</script>

<style scoped lang="scss">
.btn {
  border: none !important;
}

.outer-wrapper > div {
  box-shadow: inset 0px -1px 0px $color-secondary;
}

.header-nav-wrapper {
  min-height: 52px;
}

.centered:not(.no-flex) > *:not(.no-flex) {
  flex: 1;

  // make sure title is in the center pixel perfectly
  &.btn {
    border: none !important;
  }
}

.no-shadow {
  > div {
    box-shadow: none !important;
  }
}
.right-actions--max-width {
  max-width: 33.33%;
}

.title {
  letter-spacing: normal;
  /* stylelint-disable-next-line declaration-property-value-allowed-list */
  line-height: normal !important;

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
</style>
